import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, where, serverTimestamp, deleteDoc, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import { S, C } from "../ui";
import { TRADES } from "../constants";

// Shared by FacilityAdminDashboard (internal_staff — building engineers,
// no vendor parent, trade freely chosen) and VendorAdminDashboard
// (vendor_staff — trade fixed to the vendor's own, vendorId set). See
// spec section 3 roles table: internal_staff is "tied to Facility
// directly, no vendor — same checklist/clock-in mechanics as vendor
// staff."
export default function WorkersPanel({ facilityId, vendorId, roleType, fixedTrade, onSelect, selectedId }) {
  const [workers, setWorkers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [trade, setTrade] = useState(fixedTrade || TRADES[0]);
  const [err, setErr] = useState("");

  const filterField = vendorId ? "vendorId" : "facilityId";
  const filterValue = vendorId || facilityId;

  useEffect(() => onSnapshot(query(collection(db, "workers"), where(filterField, "==", filterValue)), s =>
    setWorkers(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(w => w.roleType === roleType))), [filterField, filterValue, roleType]);

  const addWorker = async () => {
    if (!name.trim()) { setErr("Enter a name."); return; }
    if (!/^\d{4,8}$/.test(pin)) { setErr("PIN must be 4-8 digits."); return; }
    setErr("");
    try {
      await addDoc(collection(db, "workers"), {
        facilityId, vendorId: vendorId || null, roleType,
        trade: fixedTrade || trade, name: name.trim(), pin, createdAt: serverTimestamp(),
      });
      setName(""); setPin(""); setAdding(false);
    } catch (e) {
      setErr("Couldn't add worker — try again.");
    }
  };

  const removeWorker = (id) => deleteDoc(doc(db, "workers", id));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: C.muted }}>
          {roleType === "internal_staff" ? "Facility-employed staff, no vendor above them." : "Your crew — only you and this facility's admin can see them."}
        </div>
        <button style={{ ...S.btn(), width: "auto", padding: "10px 18px" }} onClick={() => setAdding(a => !a)}>{adding ? "Cancel" : "+ Add Worker"}</button>
      </div>

      {adding && (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 0, padding: 16, marginBottom: 20, maxWidth: 420 }}>
          {err && <div style={S.err}>{err}</div>}
          <input style={S.input} placeholder="Name" value={name} onChange={e => setName(e.target.value)} autoFocus />
          <input style={S.input} placeholder="PIN (4-8 digits)" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ""))} maxLength={8} />
          {!fixedTrade && (
            <select style={{ ...S.input, background: C.white }} value={trade} onChange={e => setTrade(e.target.value)}>
              {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <button style={S.btn(C.green)} onClick={addWorker}>Add Worker</button>
        </div>
      )}

      <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        {workers.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>No workers yet.</div>
        )}
        {workers.map((w, idx) => (
          <div key={w.id} onClick={() => onSelect?.(w)}
            style={{ padding: "14px 16px", cursor: onSelect ? "pointer" : "default", background: selectedId === w.id ? C.tintAccent : "transparent", borderBottom: idx === workers.length - 1 ? "none" : `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 600 }}>{w.name}</div>
              <div style={{ fontSize: 12, color: C.muted }}>{w.trade} · PIN {w.pin}</div>
            </div>
            <button onClick={(e) => { e.stopPropagation(); removeWorker(w.id); }} className="icon-tap-target" style={S.iconBtn}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}
